import clsx from 'clsx'
import { useCallback, useContext, useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import isEmail from 'validator/es/lib/isEmail'
import { Context } from 'src/Subscribe_'

type FieldValues = {
  purchaser_email_address: string
  purchaser_name: string
  referred_by: string
  referred_by_other: string
}

const PurchaserInformation = ({ banner }: { banner: string | null }) => {
  const { dispatch, state } = useContext(Context)

  const {
    clearErrors,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      purchaser_email_address: state.values.purchaser_email_address || '',
      purchaser_name: state.values.purchaser_name || '',
      referred_by: state.values.referred_by || '',
      referred_by_other: state.values.referred_by_other || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const onSubmit: SubmitHandler<FieldValues> = useCallback((values) => {
    dispatch({
      payload: {
        values,
      },
      type: 'purchaser information continue clicked',
    })
  }, [])

  const handleBackClick = useCallback(() => {
    dispatch({
      payload: {
        values: getValues(),
      },
      type: 'purchaser information back clicked',
    })
  }, [])

  useEffect(() => {
    if (state.serverErrors.renewal_exists?.purchaser_email_address) {
      setError('purchaser_email_address', {
        message:
          'You have a pending renewal. Please visit your <a href="/renew">renew page</a> (you may have to log in first). Please email us at <a href="mailto:nureads@jewishbooks.org">nureads@jewishbooks.org</a> with any questions.',
      })
    } else if (
      state.serverErrors.subscribers_already_exist?.purchaser_email_address
    ) {
      setError('purchaser_email_address', {
        message:
          'You already have a subscription. Please email us at <a href="mailto:nureads@jewishbooks.org">nureads@jewishbooks.org</a> with any questions.',
      })
    } else {
      clearErrors('purchaser_email_address')
    }
  }, [
    state.serverErrors.renewal_exists?.purchaser_email_address,
    state.serverErrors.subscribers_already_exist?.purchaser_email_address,
  ])

  return (
    <form className="page page2 show" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <fieldset className="form-item" id="edit-your-name-and-email">
          <legend>
            <span>Your full name and email address*</span>
          </legend>
          <div>
            <div className="form-item">
              <label htmlFor="edit-purchaser-name">Full name</label>
              <input
                className={clsx('form-text', errors.purchaser_name && 'error')}
                id="edit-purchaser-name"
                placeholder=" "
                size={60}
                {...register('purchaser_name', {
                  validate: (value) => {
                    if (!value) {
                      return 'Field is required'
                    }
                    if (value.length > 35) {
                      return 'Field is limited to 35 characters'
                    }
                    return undefined
                  },
                })}
              />
              {errors.purchaser_name && (
                <div className="form-item--error-message">
                  {errors.purchaser_name.message}
                </div>
              )}
            </div>
            <div className="form-item">
              <label htmlFor="edit-purchaser-email-address">
                Email address
              </label>
              <input
                className={clsx(
                  'form-email',
                  errors.purchaser_email_address && 'error'
                )}
                id="edit-purchaser-email-address"
                placeholder=" "
                size={60}
                {...register('purchaser_email_address', {
                  validate: (value) => {
                    if (!value) {
                      return 'Field is required'
                    }
                    if (value.length > 50) {
                      return 'Field is limited to 50 characters'
                    }
                    if (!isEmail(value)) {
                      return 'Valid email required'
                    }
                    return undefined
                  },
                })}
              />
              {errors.purchaser_email_address?.message && (
                <div
                  className="form-item--error-message"
                  dangerouslySetInnerHTML={{
                    __html: errors.purchaser_email_address.message,
                  }}
                />
              )}
            </div>
          </div>
        </fieldset>
      </div>
      <div>
        <fieldset
          className="form-item"
          id="edit-how-did-you-hear-about-nu-reads"
        >
          <legend>
            <span>How did you hear about Nu Reads?*</span>
          </legend>
          <div>
            <div className="form-item js-form-type-select">
              <select
                className={clsx('form-select', errors.referred_by && 'error')}
                id="edit-referred-by"
                {...register('referred_by', { required: 'Field is required' })}
              >
                <option value="">Select</option>
                <option value="Facebook">Facebook</option>
                <option value="Instagram">Instagram</option>
                <option value="Google">Google</option>
                <option value="Friend or colleague">Friend or colleague</option>
                <option value="Jewish Book Council">Jewish Book Council</option>
                <option value="Other">Other</option>
              </select>
              {errors.referred_by && (
                <div className="form-item--error-message">
                  {errors.referred_by.message}
                </div>
              )}
            </div>
            {watch('referred_by') === 'Other' && (
              <div className="form-item">
                <input
                  className={clsx(
                    'form-text',
                    errors.referred_by_other && 'error'
                  )}
                  id="edit-referred-by-other"
                  placeholder="News article, name of book club, etc."
                  {...register('referred_by_other', {
                    required:
                      watch('referred_by') === 'Other'
                        ? 'Field is required'
                        : false,
                  })}
                />
                {errors.referred_by_other && (
                  <div className="form-item--error-message">
                    {errors.referred_by_other.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </div>
      <div
        className="field--name-field-banner"
        dangerouslySetInnerHTML={{ __html: banner || '' }}
      />
      <div className="navigation">
        <button className="continue show" type="submit">
          Continue
        </button>
        <button className="back show" onClick={handleBackClick} type="button">
          Back
        </button>
      </div>
    </form>
  )
}

export default PurchaserInformation
